/**
 * Balanced integer share allocation from TP percentage weights.
 * Matches generateBracketOrdersFromTemplate + BracketTemplateSelector.
 *
 * @param {number} totalQty
 * @param {Array<{ percentage?: number }>} takeProfits
 * @returns {number[]}
 */
export function computeBracketTpQuantities(totalQty, takeProfits) {
  if (!Array.isArray(takeProfits) || takeProfits.length === 0) {
    return [];
  }
  if (!totalQty) {
    return takeProfits.map(() => 0);
  }

  const tpBaseQuantities = takeProfits.map((tp) =>
    Math.floor(((Number(tp.percentage) || 0) / 100) * totalQty),
  );
  let remainder = totalQty - tpBaseQuantities.reduce((s, q) => s + q, 0);
  const tpQuantities = [...tpBaseQuantities];

  if (remainder > 0) {
    const fractionalParts = takeProfits.map((tp, index) => ({
      index,
      fractionalPart:
        ((Number(tp.percentage) || 0) / 100) * totalQty - tpBaseQuantities[index],
      baseQty: tpBaseQuantities[index],
    }));
    fractionalParts.sort((a, b) => {
      if (Math.abs(a.fractionalPart - b.fractionalPart) > 0.0001) {
        return b.fractionalPart - a.fractionalPart;
      }
      return a.index - b.index;
    });
    for (let i = 0; i < remainder && i < fractionalParts.length; i++) {
      tpQuantities[fractionalParts[i].index]++;
    }
  }

  return tpQuantities;
}

/** Percentages sum to exactly 100 (for N-way split when no template matches). */
export function evenSplitTakeProfitPercents(count) {
  if (count <= 0) return [];
  const base = Math.floor(100 / count);
  const rem = 100 - base * count;
  return Array.from({ length: count }, (_, i) => ({
    percentage: base + (i < rem ? 1 : 0),
    rMultiple: 1 + i,
  }));
}

/**
 * Effective TP config for an existing-position bracket: overrides, then selected
 * template, then even split. Requires percentages to sum to 100 when using
 * overrides/template.
 */
export function resolvePositionBracketTakeProfitConfig(
  tpCount,
  orderBracketOverrides,
  bracketTemplate,
) {
  if (!tpCount || tpCount < 1) return [];

  const tryPick = (arr) => {
    if (!Array.isArray(arr) || arr.length !== tpCount) return null;
    const total = arr.reduce((s, tp) => s + (Number(tp.percentage) || 0), 0);
    if (total !== 100) return null;
    return arr.map((tp, i) => ({
      percentage: Number(tp.percentage) || 0,
      rMultiple: tp.rMultiple != null ? Number(tp.rMultiple) : i + 1,
    }));
  };

  const fromOv = tryPick(orderBracketOverrides?.takeProfits);
  if (fromOv) return fromOv;
  const fromTpl = tryPick(bracketTemplate?.takeProfits);
  if (fromTpl) return fromTpl;
  return evenSplitTakeProfitPercents(tpCount);
}
